import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Spinner, Pagination, Alert } from 'react-bootstrap';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getCsvLeads } from '../../services/api';

const CsvDetailPage = () => {
  const { csvId } = useParams();
  const navigate = useNavigate();
  
  const [leads, setLeads] = useState([]);
  const [fileName, setFileName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  
  const PAGE_SIZE = 20;

  useEffect(() => {
    const fetchCsvLeads = async () => {
      setLoading(true);
      try {
        const data = await getCsvLeads(csvId, {
          skip: (page - 1) * PAGE_SIZE,
          limit: PAGE_SIZE
        });
        setLeads(data.leads);
        setFileName(data.file_name);
        setTotalRecords(data.total);
        setTotalPages(Math.ceil(data.total / PAGE_SIZE));
      } catch (err) {
        console.error('Error fetching CSV leads:', err);
        setError('Failed to load leads for this CSV file. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchCsvLeads();
  }, [csvId, page]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  const renderCibilStatus = (status) => {
    if (status === 'success') {
      return <Badge bg="success">Success</Badge>;
    } else if (status === 'failure') {
      return <Badge bg="danger">Failed</Badge>;
    } else {
      return <Badge bg="warning">Pending</Badge>;
    }
  };

  if (loading && leads.length === 0) {
    return (
      <div className="d-flex justify-content-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  const renderAction = (action, uploadType) => {
    if (action === "created") {
      return <Badge bg="success">Created</Badge>;
    } else if (action === "updated") {
      return <Badge bg="info">Updated</Badge>;
    } else {
      return <Badge bg="secondary">Unknown</Badge>;
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>{fileName || 'CSV File Details'}</h2>
        <div>
          <Button 
            variant="outline-secondary" 
            className="me-2"
            onClick={() => navigate('/csvs')}
          >
            Back to CSV Files
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card>
        <Card.Body>
          <div className="mb-3">
            <h5>Leads ({totalRecords})</h5>
          </div>

          {leads.length === 0 ? (
            <div className="text-center p-5">
              <p>No leads found in this CSV file.</p>
            </div>
          ) : (
            <>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Mobile</th>
                    <th>PAN</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>CIBIL Status</th>
                    <th>Action</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map(lead => (
                    <tr key={lead.id}>
                      <td>{lead.name}</td>
                      <td>{lead.mobile}</td>
                      <td>{lead.pan || '-'}</td>
                      <td>{lead.email || '-'}</td>
                      <td>
                        <Badge bg={lead.status === 'valid' ? 'success' : 'secondary'}>
                          {lead.status}
                        </Badge>
                      </td>
                      <td>{renderCibilStatus(lead.cibil_status)}</td>
                      <td>{renderAction(lead.action, lead.upload_type)}</td>
                      <td>{formatDate(lead.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <Pagination>
                    <Pagination.First 
                      onClick={() => setPage(1)} 
                      disabled={page === 1} 
                    />
                    <Pagination.Prev 
                      onClick={() => setPage(prev => Math.max(prev - 1, 1))} 
                      disabled={page === 1} 
                    />
                    
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }
                      
                      return (
                        <Pagination.Item 
                          key={pageNum} 
                          active={pageNum === page}
                          onClick={() => setPage(pageNum)}
                        >
                          {pageNum}
                        </Pagination.Item>
                      );
                    })}
                    
                    <Pagination.Next 
                      onClick={() => setPage(prev => Math.min(prev + 1, totalPages))} 
                      disabled={page === totalPages} 
                    />
                    <Pagination.Last 
                      onClick={() => setPage(totalPages)} 
                      disabled={page === totalPages} 
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default CsvDetailPage;